/**
 * The record history drawer (wave AUD frontend item 1).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Opened from every record screen (project, contract, party, ITP and ITP item, schedule,
 * activity, request, finding, punch list, action request, credential, decision rule,
 * checklist bank). Reads GET /api/audit/{TYPE}/{id} up front:
 * - When the API answers 404 (the person lacks AUDIT_LOG READ or cannot see the record),
 *   the control is not offered at all, never an empty drawer.
 * - When the API answers 200, the button opens the drawer showing one row per entry newest
 *   first: actor display name (or the worker's name), the instant in the tenant's zone,
 *   the event, entries of parts (parent_type set) labelled by their table, and the changes
 *   list rendered as "field: before -> after".
 */

import { useState } from "react";

import {
  getRecordHistory,
  type AuditChange,
  type AuditEntry,
  type AuditRecordType,
  type RecordHistory,
} from "../api/audit";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { zonedStamp } from "../lib/zonedTime";
import { ActionDialog } from "../nav/ActionDialog";
import { useQuery } from "../screens/useQuery";

export function formatAuditValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "null";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

export function formatAuditChange(change: AuditChange): string {
  return strings.audit.changeLine(
    change.field,
    formatAuditValue(change.before),
    formatAuditValue(change.after),
  );
}

export function sortHistoryNewestFirst(items: readonly AuditEntry[]): AuditEntry[] {
  return [...items].sort((a, b) => {
    const byTime = b.createdAt.localeCompare(a.createdAt);
    if (byTime !== 0) {
      return byTime;
    }
    return b.seq - a.seq;
  });
}

export function HistoryDrawer({
  history,
  recordLabel,
  onClose,
}: {
  readonly history: RecordHistory;
  readonly recordLabel?: string | undefined;
  readonly onClose: () => void;
}): React.JSX.Element {
  const copy = strings.audit;
  const ordered = sortHistoryNewestFirst(history.items);

  return (
    <ActionDialog open title={copy.historyHeading(recordLabel)} onClose={onClose} error={null}>
      <div className="q-ui flex flex-col gap-3" data-testid="record-history-drawer">
        {ordered.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="record-history-empty">
            {copy.historyEmpty}
          </p>
        ) : (
          <ol className="flex flex-col gap-2" data-testid="record-history-list">
            {ordered.map((entry) => {
              const actor = entry.actorDisplayName ?? entry.actorMechanism ?? copy.unknownActor;
              const when = zonedStamp(entry.createdAt);
              const partTable =
                typeof entry.payload.table === "string" && entry.payload.table !== ""
                  ? entry.payload.table
                  : entry.objectType;
              return (
                <li
                  key={entry.id}
                  className="flex flex-col gap-1 rounded border border-border p-2.5 text-sm"
                  data-testid="record-history-entry"
                  data-seq={String(entry.seq)}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium" data-testid="record-history-actor">
                        {actor}
                      </span>
                      <span className="q-chip" data-testid="record-history-event">
                        {entry.eventType}
                      </span>
                      {entry.parentType !== null && (
                        <span className="q-chip" data-testid="record-history-part">
                          {copy.historyPart(partTable, entry.objectType)}
                        </span>
                      )}
                    </div>
                    <span
                      className="q-mono text-xs text-muted-foreground"
                      data-testid="record-history-when"
                    >
                      {when}
                    </span>
                  </div>
                  {entry.changes.length > 0 && (
                    <ul
                      className="flex flex-col gap-0.5 text-xs text-muted-foreground"
                      data-testid="record-history-changes"
                    >
                      {entry.changes.map((change) => (
                        <li
                          key={change.field}
                          className="q-mono"
                          data-testid="record-history-change"
                        >
                          {formatAuditChange(change)}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        )}
        <div className="q-actions-row">
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="record-history-close"
            onClick={onClose}
          >
            {copy.close}
          </button>
        </div>
      </div>
    </ActionDialog>
  );
}

export function HistoryButton({
  objectType,
  objectId,
  recordLabel,
  testId = "record-history-open",
}: {
  readonly objectType: AuditRecordType;
  readonly objectId: string;
  readonly recordLabel?: string | undefined;
  readonly testId?: string | undefined;
}): React.JSX.Element | null {
  const token = useToken();
  const [open, setOpen] = useState(false);
  const state = useQuery(
    (signal) => getRecordHistory(token, objectType, objectId, signal),
    [token, objectType, objectId],
  );

  if (state.kind !== "ready") {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="q-button q-button--quiet"
        data-testid={testId}
        data-object-type={objectType}
        data-object-id={objectId}
        onClick={() => {
          setOpen(true);
        }}
      >
        {strings.audit.historyButton}
      </button>
      {open && (
        <HistoryDrawer
          history={state.data}
          recordLabel={recordLabel}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );
}
